import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
  FaSearch,
  FaEye,
  FaUniversity,
  FaCopy,
  FaEnvelope,
  FaSync,
} from "react-icons/fa";
import { getAllWithdrawals } from "api/ApiServices.js";

const getAvatarUrl = (user, fallbackName) => {
  if (user?.image && !user.image.includes("avatar-1.jpg")) {
    if (user.image.startsWith("http")) return user.image;
    return `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${user.image}`;
  }
  const name = user?.name || fallbackName || "User";
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=5e72e4&color=fff`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return { date: "—", time: "" };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { date: String(dateStr), time: "" };
    const date = d.toISOString().split("T")[0];
    const time = d.toTimeString().split(" ")[0];
    return { date, time };
  } catch (e) {
    return { date: String(dateStr), time: "" };
  }
};

const WithdrawFundManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Summary Metrics
  const [metrics, setMetrics] = useState({
    totalVolume: 0,
    totalCount: 0,
    pendingVolume: 0,
    pendingCount: 0,
    approvedVolume: 0,
    approvedCount: 0,
    rejectedCount: 0,
  });

  // Selected withdrawal for Detail Modal
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch dynamic withdrawals from API
  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllWithdrawals(
        currentPage,
        itemsPerPage,
        searchTerm,
        statusFilter === "All" ? "" : statusFilter
      );

      if (response && response.payload) {
        setWithdrawals(response.payload.withdrawals || []);
        setTotalPages(response.payload.totalPages || 1);
        setTotalCount(response.payload.total || 0);
        if (response.payload.metrics) {
          setMetrics(response.payload.metrics);
        }
      } else {
        setWithdrawals([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching dynamic withdrawals:", err);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  // Modal Handlers
  const handleOpenModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWithdrawal(null);
  };

  // Copy helper
  const handleCopyText = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `${label} copied to clipboard`,
      showConfirmButton: false,
      timer: 1800,
    });
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "approved":
      case "completed":
        return (
          <Badge color="success" className="badge-dot">
            <i className="bg-success" /> Approved
          </Badge>
        );
      case "pending":
        return (
          <Badge color="warning" className="badge-dot">
            <i className="bg-warning" /> Pending
          </Badge>
        );
      case "processing":
        return (
          <Badge color="info" className="badge-dot">
            <i className="bg-info" /> Processing
          </Badge>
        );
      case "rejected":
      case "failed":
        return (
          <Badge color="danger" className="badge-dot">
            <i className="bg-danger" /> Rejected
          </Badge>
        );
      default:
        return <Badge color="secondary">{status || "Pending"}</Badge>;
    }
  };

  return (
    <>
      <CommonHeader />

      <Container fluid className="mt--7">
        {/* MAIN CONTROLS & TABLE CARD */}
        <Row>
          <Col>
            <Card className="shadow border-0" style={{ borderRadius: "16px", overflow: "hidden" }}>
              {/* CARD HEADER & FILTERS */}
              <CardHeader className="border-0 bg-white pt-4 pb-3">
                <Row className="align-items-center">
                  <Col md="6">
                    <h3 className="mb-0 text-dark font-weight-bold d-flex align-items-center">
                      <FaUniversity className="text-primary mr-2" /> Withdraw Fund Management
                    </h3>
                  </Col>
                  <Col md="6" className="text-md-right mt-2 mt-md-0">
                    <Button
                      color="secondary"
                      size="sm"
                      className="btn-round shadow-sm"
                      onClick={() => fetchWithdrawals()}
                      disabled={loading}
                    >
                      <FaSync className={`mr-1 ${loading ? "fa-spin" : ""}`} /> Refresh
                    </Button>
                  </Col>
                </Row>

                <hr className="my-3" />

                {/* FILTER BAR */}
                <Row className="align-items-center">
                  {/* Search Input */}
                  <Col lg="5" md="6" className="mb-2 mb-lg-0">
                    <InputGroup className="input-group-alternative input-group-merge border rounded-lg">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <FaSearch className="text-muted" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Search Username, Email, Bank, ID..."
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </InputGroup>
                  </Col>

                  {/* Status Filter Buttons */}
                  <Col lg="7" md="6" className="d-flex flex-wrap justify-content-md-end" style={{ gap: "6px" }}>
                    {["All", "Pending", "Processing", "Approved", "Rejected"].map((st) => (
                      <Button
                        key={st}
                        size="sm"
                        color={statusFilter === st ? "primary" : "secondary"}
                        outline={statusFilter !== st}
                        className="px-3 py-1 font-weight-600 rounded-pill shadow-none"
                        onClick={() => {
                          setStatusFilter(st);
                          setCurrentPage(1);
                        }}
                      >
                        {st}
                        {st === "Pending" && metrics.pendingCount > 0 && (
                          <span className="badge badge-warning badge-pill ml-1 text-dark">
                            {metrics.pendingCount}
                          </span>
                        )}
                      </Button>
                    ))}
                  </Col>
                </Row>
              </CardHeader>

              {/* TABLE LISTING */}
              <div className="table-responsive">
                <Table className="align-items-center table-flush" hover>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col" style={{ fontSize: "11px" }}>Withdrawal ID</th>
                      <th scope="col" style={{ fontSize: "11px" }}>User & Email</th>
                      <th scope="col" style={{ fontSize: "11px" }}>Amount</th>
                      <th scope="col" style={{ fontSize: "11px" }}>Date & Time</th>
                      <th scope="col" style={{ fontSize: "11px" }}>Bank Details</th>
                      <th scope="col" style={{ fontSize: "11px" }}>Status</th>
                      <th scope="col" style={{ fontSize: "11px" }} className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <Spinner color="primary" />
                          <div className="text-muted mt-2 font-weight-bold">Loading withdrawal requests...</div>
                        </td>
                      </tr>
                    ) : withdrawals.length > 0 ? (
                      withdrawals.map((item) => {
                        const id = item.withdrawalId || item.id || item._id;
                        const user = item.userId || {};
                        const userName = user.name || item.username || user.handlername || "Unknown User";
                        const userEmail = user.email || item.userEmail || "N/A";
                        const avatar = getAvatarUrl(user, userName);
                        const amount = Number(item.amount) || 0;
                        const fee = Number(item.fee) || 0;
                        const netAmount = Number(item.netAmount) || (amount - fee);
                        const method = item.method || item.paymentMethod || "Bank Transfer";
                        const bankName = item.bankDetails?.bankName || item.bankDetail?.bankName || (item.paypalDetails?.email ? "PayPal" : "Bank Transfer");
                        const accountHolder = item.bankDetails?.accountHolder || item.bankDetails?.accountName || item.bankDetail?.accountHolder || userName;
                        const accountNumber = item.bankDetails?.accountNumber || item.bankDetail?.accountNumber || item.paypalDetails?.email || "";
                        const dateFormatted = formatDate(item.requestedAt || item.createdAt || item.date);

                        return (
                          <tr key={item._id || id} style={{ transition: "all 0.2s" }}>
                            {/* Withdrawal ID */}
                            <td>
                              <span className="font-weight-bold text-primary" style={{ fontSize: "13px" }}>
                                {id}
                              </span>
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {method}
                              </div>
                            </td>

                            {/* Username & Email */}
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={avatar}
                                  alt={userName}
                                  className="rounded-circle mr-2 border shadow-sm"
                                  style={{ width: "38px", height: "38px", objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=5e72e4&color=fff`;
                                  }}
                                />
                                <div>
                                  <div className="font-weight-bold text-dark" style={{ fontSize: "13px" }}>
                                    {userName}
                                  </div>
                                  <div className="text-muted" style={{ fontSize: "11px" }}>
                                    {userEmail}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Amount */}
                            <td>
                              <div className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                Fee: ${fee.toFixed(2)} | Net: ${netAmount.toFixed(2)}
                              </div>
                            </td>

                            {/* Date */}
                            <td>
                              <div className="text-dark font-weight-500" style={{ fontSize: "12px" }}>
                                {dateFormatted.date}
                              </div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {dateFormatted.time}
                              </div>
                            </td>

                            {/* Bank Detail Summary */}
                            <td>
                              <div className="font-weight-bold text-dark" style={{ fontSize: "12px" }}>
                                <FaUniversity className="text-muted mr-1" /> {bankName}
                              </div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {accountHolder} {accountNumber ? `(•••• ${accountNumber.slice(-4)})` : ""}
                              </div>
                            </td>

                            {/* Status */}
                            <td>
                              {getStatusBadge(item.status)}
                            </td>

                            {/* Action: View Button Only */}
                            <td className="text-right">
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                className="px-2 py-1 shadow-sm"
                                title="View Detail Modal"
                                onClick={() => handleOpenModal(item)}
                              >
                                <FaEye className="mr-1" /> View
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted">
                          <FaUniversity size={36} className="text-muted mb-2 opacity-50" />
                          <p className="mb-0 font-weight-bold">No withdrawal requests found matching your filters.</p>
                          <small>Try clearing your search query or selecting 'All' status filter.</small>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* CARD FOOTER WITH PAGINATION */}
              <CardFooter className="py-3 bg-white d-flex align-items-center justify-content-between">
                <div className="text-muted text-sm">
                  Showing <span className="font-weight-bold">{withdrawals.length}</span> of{" "}
                  <span className="font-weight-bold">{totalCount}</span> entries
                </div>

                {totalPages > 1 && (
                  <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink
                        previous
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => (
                      <PaginationItem active={i + 1 === currentPage} key={i + 1}>
                        <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink
                        next
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      />
                    </PaginationItem>
                  </Pagination>
                )}
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ═══════════════════════════════════════════════════
          WITHDRAWAL DETAIL MODAL ("VIEW MODAL")
          ═══════════════════════════════════════════════════ */}
      {selectedWithdrawal && (() => {
        const selId = selectedWithdrawal.withdrawalId || selectedWithdrawal.id || selectedWithdrawal._id;
        const selUser = selectedWithdrawal.userId || {};
        const selUserName = selUser.name || selectedWithdrawal.username || selUser.handlername || "Unknown User";
        const selEmail = selUser.email || selectedWithdrawal.userEmail || "N/A";
        const selAvatar = getAvatarUrl(selUser, selUserName);
        const selAmount = Number(selectedWithdrawal.amount) || 0;
        const selFee = Number(selectedWithdrawal.fee) || 0;
        const selNet = Number(selectedWithdrawal.netAmount) || (selAmount - selFee);
        const selDate = formatDate(selectedWithdrawal.requestedAt || selectedWithdrawal.createdAt || selectedWithdrawal.date);
        const selBank = selectedWithdrawal.bankDetails || selectedWithdrawal.bankDetail || {};
        const isPayPal = !!selectedWithdrawal.paypalDetails?.email;

        return (
          <Modal
            isOpen={isModalOpen}
            toggle={handleCloseModal}
            size="lg"
            centered
            className="modal-dialog-centered"
          >
            <ModalHeader toggle={handleCloseModal} className="bg-gradient-primary text-white border-0 py-3">
              <div className="d-flex align-items-center">
                <FaUniversity className="mr-2" size={20} />
                <div>
                  <h4 className="modal-title text-white mb-0 font-weight-bold">
                    Withdrawal Details - {selId}
                  </h4>
                  <small className="text-white-50">
                    Requested on {selDate.date} {selDate.time}
                  </small>
                </div>
              </div>
            </ModalHeader>

            <ModalBody className="p-4" style={{ backgroundColor: "#f8f9fe" }}>
              {/* TOP HEADER SUMMARY BAR */}
              <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: "12px", background: "#ffffff" }}>
                <CardBody className="p-3">
                  <Row className="align-items-center">
                    <Col md="6">
                      <div className="d-flex align-items-center">
                        <img
                          src={selAvatar}
                          alt={selUserName}
                          className="rounded-circle mr-3 border shadow-sm"
                          style={{ width: "52px", height: "52px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selUserName)}&background=5e72e4&color=fff`;
                          }}
                        />
                        <div>
                          <h4 className="mb-0 font-weight-bold text-dark">
                            {selUserName}
                          </h4>
                          <div className="text-muted text-sm d-flex align-items-center mt-1">
                            <FaEnvelope className="mr-1 text-primary" /> {selEmail}
                          </div>
                          {selUser.handlername && (
                            <small className="text-muted">@{selUser.handlername}</small>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md="6" className="text-md-right mt-3 mt-md-0">
                      <div className="text-muted text-xs text-uppercase font-weight-bold">
                        Requested Amount
                      </div>
                      <div className="h2 font-weight-bold text-primary mb-0">
                        ${selAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                      <div className="text-muted text-xs">
                        Fee: ${selFee.toFixed(2)} | Net: ${selNet.toFixed(2)}
                      </div>
                      <div className="mt-1">{getStatusBadge(selectedWithdrawal.status)}</div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* PAYMENT / BANK DETAILS SECTION */}
              <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                <CardHeader className="bg-transparent border-0 pb-0 pt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="text-uppercase text-muted font-weight-bold mb-0" style={{ fontSize: "12px" }}>
                      <FaUniversity className="mr-1 text-primary" /> Recipient Account Details
                    </h5>
                    <Badge color="primary" className="px-2 py-1">
                      {isPayPal ? "PayPal Destination" : "Direct Deposit Destination"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody className="pt-2">
                  {isPayPal ? (
                    <Row>
                      <Col md="6">
                        <div className="py-2 border-bottom d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted d-block">PayPal Email</small>
                            <strong className="text-dark">{selectedWithdrawal.paypalDetails.email}</strong>
                          </div>
                          <Button
                            color="secondary"
                            size="sm"
                            className="px-2 py-1 border-0"
                            onClick={() => handleCopyText(selectedWithdrawal.paypalDetails.email, "PayPal Email")}
                          >
                            <FaCopy className="text-muted" />
                          </Button>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="py-2 border-bottom">
                          <small className="text-muted d-block">Payout Batch ID</small>
                          <strong className="text-dark">{selectedWithdrawal.paypalDetails.payoutBatchId || "N/A"}</strong>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col md="6">
                        <div className="py-2 border-bottom">
                          <small className="text-muted d-block">Bank Name</small>
                          <strong className="text-dark">{selBank.bankName || "N/A"}</strong>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="py-2 border-bottom">
                          <small className="text-muted d-block">Account Holder Name</small>
                          <strong className="text-dark">{selBank.accountHolder || selBank.accountName || selUserName}</strong>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="py-2 border-bottom d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted d-block">Account / IBAN Number</small>
                            <strong className="text-dark">{selBank.accountNumber || "N/A"}</strong>
                          </div>
                          {selBank.accountNumber && (
                            <Button
                              color="secondary"
                              size="sm"
                              className="px-2 py-1 border-0"
                              title="Copy Account Number"
                              onClick={() => handleCopyText(selBank.accountNumber, "Account Number")}
                            >
                              <FaCopy className="text-muted" />
                            </Button>
                          )}
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="py-2 border-bottom d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted d-block">Routing / IFSC / Sort Code</small>
                            <strong className="text-dark">{selBank.routingNumber || selBank.bankCode || "N/A"}</strong>
                          </div>
                          {(selBank.routingNumber || selBank.bankCode) && (
                            <Button
                              color="secondary"
                              size="sm"
                              className="px-2 py-1 border-0"
                              title="Copy Routing Number"
                              onClick={() => handleCopyText(selBank.routingNumber || selBank.bankCode, "Routing Code")}
                            >
                              <FaCopy className="text-muted" />
                            </Button>
                          )}
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="py-2">
                          <small className="text-muted d-block">SWIFT / BIC Code</small>
                          <span className="font-weight-bold text-dark">{selBank.swiftCode || "N/A"}</span>
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="py-2">
                          <small className="text-muted d-block">Account Type</small>
                          <span className="font-weight-bold text-dark">{selBank.accountType || "Checking"}</span>
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="py-2">
                          <small className="text-muted d-block">Country & Branch</small>
                          <span className="font-weight-bold text-dark">
                            {selBank.branchCity ? `${selBank.branchCity}, ` : ""}{selBank.country || "United States"}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>
            </ModalBody>

            <ModalFooter className="bg-white border-0 py-3 d-flex justify-content-end">
              <Button color="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        );
      })()}
    </>
  );
};

export default WithdrawFundManagement;
